/**
 * Incremental partial-JSON completer for `tool_input_delta` SSE events
 * (agent-ux-optimization Phase 2 Session 1; directive §9.3).
 *
 * The streaming hook receives `partial_input_json` chunks of arbitrary length
 * — sometimes mid-key, mid-value, mid-number, or mid-string. After each
 * chunk we want a best-effort parsed object so renderers can paint partial
 * skeletons (e.g. `<Sensitivity2DMatrix>` axis labels arriving before
 * dimensions). A naive "reparse from byte 0 every delta" implementation is
 * O(n²) per turn and visibly janky for large `set_inputs` payloads.
 *
 * This module maintains parser state ACROSS deltas — a stack of unclosed
 * structures + in-string + escape-next flags — so each call processes only
 * the new delta. After walking, synthesize closers for the remaining stack
 * and `JSON.parse` the synthesis once. On parse failure (rare — typically
 * mid-number or mid-keyword), return the previous successful parse so the
 * caller renders a stale-but-coherent snapshot until the next delta resolves
 * the partial token.
 *
 * Reference: Aha! Engineering's `JsonCompleter` writeup (Ruby, but the
 * algorithm ports cleanly).
 */

export interface ParserState {
  /** Accumulated raw JSON across all deltas seen so far. */
  rawJson: string;
  /**
   * Stack of unclosed structures — `{` (object), `[` (array), `"` (string).
   * Pushed on open, popped on matching close. Synthesized in reverse order
   * to terminate a partial document for parsing.
   */
  stack: Array<'{' | '[' | '"'>;
  /** True while inside a string literal — stack braces don't push when set. */
  inString: boolean;
  /** True when the previous char was `\` inside a string. Skips the next char. */
  escapeNext: boolean;
  /**
   * Last successful parse result. Returned on parse failures so the caller
   * keeps rendering the prior snapshot until the next delta resolves the
   * partial token.
   */
  lastParsed: object;
}

/** Initialize a fresh parser state. Caller persists this across deltas. */
export function newPartialJsonState(): ParserState {
  return {
    rawJson: '',
    stack: [],
    inString: false,
    escapeNext: false,
    lastParsed: {},
  };
}

/**
 * Append a delta and return the best-effort parsed object.
 *
 * The state is mutated in place AND returned (callers can ignore the
 * returned reference; React-side reducers should treat the parsed object as
 * the value of interest, not the state itself).
 */
export function appendDelta(
  state: ParserState,
  delta: string,
): { state: ParserState; parsed: object } {
  // 1. Walk delta char-by-char; mutate stack/inString/escapeNext.
  for (const ch of delta) {
    if (state.escapeNext) {
      state.escapeNext = false;
      continue;
    }
    if (state.inString) {
      if (ch === '\\') {
        state.escapeNext = true;
      } else if (ch === '"') {
        state.inString = false;
        // Pop the matching string frame (always the top).
        if (state.stack[state.stack.length - 1] === '"') state.stack.pop();
      }
      // Other chars are content within the string — no stack effect.
      continue;
    }
    // Outside string mode.
    if (ch === '"') {
      state.inString = true;
      state.stack.push('"');
    } else if (ch === '{') {
      state.stack.push('{');
    } else if (ch === '[') {
      state.stack.push('[');
    } else if (ch === '}' || ch === ']') {
      // Pop matching frame; tolerate mismatch (mid-stream junk).
      if (state.stack.length > 0) state.stack.pop();
    }
    // Whitespace / numbers / colons / commas — no stack effect.
  }
  state.rawJson += delta;

  // 2. Synthesize closers. Drop a trailing comma (outside any string) before
  //    closing — `{"a":1,}` is invalid JSON. Mid-number deltas (no trailing
  //    comma) close cleanly because numbers terminate at a structural char.
  let synth = state.rawJson;
  if (!state.inString) {
    const trimmed = synth.replace(/\s+$/, '');
    if (trimmed.endsWith(',')) synth = trimmed.slice(0, -1);
  }
  for (let i = state.stack.length - 1; i >= 0; i--) {
    const frame = state.stack[i];
    synth += frame === '{' ? '}' : frame === '[' ? ']' : '"';
  }

  // 3. Parse. Success: store + return. Failure: return prior snapshot.
  try {
    const parsed: unknown = JSON.parse(synth);
    if (parsed !== null && typeof parsed === 'object') {
      state.lastParsed = parsed as object;
      return { state, parsed: parsed as object };
    }
  } catch {
    // Mid-number / mid-keyword / unsynthesizable shape — fall through.
  }
  return { state, parsed: state.lastParsed };
}
